import { getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { useFetcher, useRouteLoaderData } from '@remix-run/react'
import { useEffect, useRef, useState } from 'react'
import {
  Container,
  Row,
  Col,
  FormGroup,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  Button,
} from 'reactstrap'
import { z } from 'zod'
import { InputMask } from '#app/components/forms/InputMask.tsx'
import { phoneMask } from '#app/components/forms/masks.ts'
import { SubmitButton } from '#app/components/ui/submitButton.tsx'
import { Textarea } from '#app/components/ui/textarea.tsx'
import { useRecaptcha } from '#app/hooks/useRecaptcha.ts'
import { type loader as rootLoader } from '#app/root.tsx'
import { type action } from '#app/routes/fale-conosco.ts'

// Schema para validação inicial (sem reCAPTCHA)
export const ContactMessageSchema = z.object({
  name: z.string().min(1, { message: 'Nome obrigatório' }),
  email: z
    .string()
    .min(1, { message: 'Email obrigatório' })
    .email({ message: 'Email inválido' }),
  phone: z.string().min(1, { message: 'Celular obrigatório' }),
  message: z.string().min(1, { message: 'Mensagem obrigatória' }),
})

// Schema completo para validação no servidor
export const ContactMessageSchemaWithRecaptcha = z.object({
  name: z.string().min(1, { message: 'Nome obrigatório' }),
  email: z
    .string()
    .min(1, { message: 'Email obrigatório' })
    .email({ message: 'Email inválido' }),
  phone: z.string().min(1, { message: 'Celular obrigatório' }),
  message: z.string().min(1, { message: 'Mensagem obrigatória' }),
  recaptchaToken: z
    .string()
    .min(1, { message: 'Verificação de segurança obrigatória' }),
})

export default function ContactFormSection() {
  const actionFetcher = useFetcher<typeof action>()
  const rootData = useRouteLoaderData<typeof rootLoader>('root')
  const submitedForm =
    actionFetcher.data && actionFetcher.data.status === 'success'
  const formRef = useRef<HTMLFormElement>(null)
  const [modal, setModal] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Inicializa o reCAPTCHA
  const recaptchaSiteKey = rootData?.ENV?.RECAPTCHA_SITE_KEY || ''
  const { executeRecaptcha } = useRecaptcha({
    siteKey: recaptchaSiteKey,
    action: 'contact_form',
  })

  useEffect(() => {
    if (submitedForm) {
      setModal(true)
      formRef.current?.reset()

      setIsSubmitting(false)
    }
  }, [submitedForm])

  setTimeout(() => {
    setModal(false)
  }, 5000)

  const [form, fields] = useForm({
    id: 'contact-form',
    constraint: getZodConstraint(ContactMessageSchema),
    shouldRevalidate: 'onSubmit',
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: ContactMessageSchema })
    },
    onSubmit: async (event, { formData }) => {
      event.preventDefault()

      if (isSubmitting) return

      setIsSubmitting(true)

      try {
        // Executa o reCAPTCHA
        const recaptchaToken = await executeRecaptcha()

        if (!recaptchaToken) {
          // Em desenvolvimento, permitir envio sem reCAPTCHA se não houver chave
          if (!recaptchaSiteKey) {
            formData.set('recaptchaToken', 'dev-token')
          } else {
            setIsSubmitting(false)
            return
          }
        } else {
          // Adiciona o token do reCAPTCHA ao FormData
          formData.set('recaptchaToken', recaptchaToken)
        }

        // Submete o formulário
        actionFetcher.submit(formData, {
          method: 'POST',
          action: '/fale-conosco',
        })
      } catch (error) {
        console.error('Erro ao processar formulário:', error)
        setIsSubmitting(false)
      }
    },
  })

  return (
    <div className="contact-section">
      <Container>
        <Row className="align-items-center">
          <Col lg="7" className="contact-image-col d-none d-lg-block">
            <div className="contact-image-wrapper">
              <img
                src="/images/lp/final-photo-low-res.png"
                alt="banner clube do tennis"
                className="contact-image"
              />
            </div>
          </Col>
          <Col lg="5" className="contact-form-col">
            <div className="contact-form-wrapper">
              <h2 className="contact-title">Fale conosco</h2>
              <actionFetcher.Form
                method="POST"
                action={'/fale-conosco'}
                ref={formRef}
                id={form.id}
                onSubmit={form.onSubmit}
                className="contact-form"
              >
                <FormGroup className="contact-form-group">
                  <Input
                    {...getInputProps(fields.name, { type: 'text' })}
                    placeholder="Digite seu nome"
                    autoComplete="name"
                    className="contact-input"
                  />
                </FormGroup>
                <FormGroup className="contact-form-group">
                  <Input
                    {...getInputProps(fields.email, { type: 'email' })}
                    placeholder="Digite seu e-mail"
                    autoComplete="email"
                    className="contact-input"
                  />
                </FormGroup>
                <FormGroup className="contact-form-group">
                  <InputMask
                    {...getInputProps(fields.phone, { type: 'tel' })}
                    placeholder="(00) 00000-0000"
                    autoComplete="tel"
                    mask={phoneMask}
                    className="contact-input"
                  />
                </FormGroup>
                <FormGroup className="contact-form-group">
                  <Textarea
                    {...getInputProps(fields.message, { type: 'text' })}
                    placeholder="Digite sua mensagem"
                    rows={4}
                    className="contact-textarea"
                  />
                </FormGroup>
                <SubmitButton
                  color="primary"
                  className="contact-submit-btn"
                  title={isSubmitting ? 'Enviando...' : 'Enviar'}
                  disabled={isSubmitting}
                />
              </actionFetcher.Form>
            </div>
          </Col>
        </Row>
        <ThankYouModal onClose={() => setModal(false)} isOpen={modal} />
      </Container>
    </div>
  )
}
export function ThankYouModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}) {
  return (
    <Modal isOpen={isOpen} centered>
      <ModalHeader className="border-bottom-0" toggle={onClose} />
      <ModalBody className="text-center">
        <div className="avatar-md mx-auto mb-4">
          <div className="avatar-title bg-light rounded-circle text-primary h1">
            <i className="mdi mdi-email-open" />
          </div>
        </div>
        <h5>Obrigado por sua mensagem! Em breve entraremos em contato.</h5>
        <Button
          color="primary"
          className="mt-4 fs-5"
          onClick={onClose}
          children="Fechar"
        />
      </ModalBody>
    </Modal>
  )
}
